import { deepCompare } from '@co4/graph';
import { SlIconButton as IconBtnType } from '@shoelace-style/shoelace';
import { SlCheckbox, SlIconButton } from '@shoelace-style/shoelace/dist/react';
import { memo, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DnDSubRow from './DnDSubRow';
import { ColumnData, DnDTableProps } from './DnDTable';
import DnDTableCell from './DnDTableCell';

interface DnDTableBodyRowProps<T> {
  index: number;
  rowSelection: boolean;
  expandable: boolean;
  selected: boolean;
  onCheckboxToggle: (id: string | number) => void;
  columns: Array<ColumnData>;
  data: DnDTableProps<T>['data'][0];
  subrowProps?: DnDTableProps<T>['subrowProps'];
  draggable: boolean;
  id: string;
}

function DnDTableBodyRow<T extends Record<string, any>>(props: DnDTableBodyRowProps<T>) {
  const { rowSelection, selected, onCheckboxToggle, columns, data, index, draggable, id } = props;
  const { expandable, subrowProps } = props;
  const [expanded, setExpanded] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !draggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const toggleExpansion = (e: React.MouseEvent<IconBtnType>) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const shouldExpand = typeof subrowProps?.renderer === 'function' || data?.subRows?.length > 0;

  return (
    <>
      <tr ref={setNodeRef} style={style} className={isDragging ? 'dragging' : ''}>
        {draggable && (
          <td className="drag-handle-td" {...attributes} {...listeners}>
            <SlIconButton name="grip-vertical" />
          </td>
        )}

        {rowSelection && (
          <td id={`select-box-${index}`}>
            <div id="select-all" className="selection-box head-col">
              <SlCheckbox
                checked={selected}
                onSlChange={() => {
                  onCheckboxToggle(data?.selectionId!);
                }}
              />
            </div>
          </td>
        )}

        {expandable && (
          <td className="expand-button-td">
            {shouldExpand && (
              <SlIconButton name={expanded ? 'chevron-down' : 'chevron-right'} onClick={toggleExpansion} />
            )}
          </td>
        )}

        {columns.map((val, i) =>
          val?.show === false ? null : (
            <DnDTableCell key={`value-${val.field}-${id}`} data={data} index={i} cell={val} />
          ),
        )}
      </tr>

      {/* Add subrow under the current row */}
      {expandable && shouldExpand && expanded && (
        <DnDSubRow {...subrowProps} colspan={Number(columns?.length) + 1 + (draggable ? 1 : 0)} data={data?.subRows} />
      )}
    </>
  );
}

export default memo(DnDTableBodyRow, deepCompare);
